import { useMemo } from 'react';

import { useLocation, useOutletContext, useSearchParams } from '@remix-run/react';

import type { OutletContext } from '~/interfaces/outlet_context';
import type { Paywall } from '~/interfaces/paywall';
import type { Post } from '~/interfaces/post';
import type { Site } from '~/interfaces/site';
import type { Subscription } from '~/interfaces/subscriptions';

import { PostPageContext } from './PostPageContext';

// props from PostMetaInformation
// 'hide_premium_post' | 'paywall_shown' | 'force_premium' | 'enforce_paywall_breaks' | 'contains_subscriber_break'
interface PostPageProviderProps {
  site: Site;
  post: Post;
  hide_premium_post?: boolean;
  paywall_shown?: boolean;
  force_premium?: boolean;
  enforce_paywall_breaks?: boolean;
  contains_subscriber_break?: boolean;
  children: React.ReactNode;
  subscription?: Subscription;
  previewSubscription?: Subscription;
  commentsEnabled: boolean;
  isDraft: boolean;
  onlyRenderComments: boolean;
  paywall: Paywall | null;
}

export const PostPageProvider: React.FC<PostPageProviderProps> = ({
  site,
  post,
  hide_premium_post,
  paywall_shown,
  force_premium,
  enforce_paywall_breaks,
  contains_subscriber_break,
  subscription,
  previewSubscription,
  commentsEnabled,
  isDraft,
  onlyRenderComments,
  paywall,
  children,
}) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isPreviewModal = searchParams.get('preview_modal') === 'true';
  // override meta information if they're provided as a prop
  hide_premium_post = hide_premium_post ?? post.hide_premium_post;
  paywall_shown = paywall_shown ?? post.paywall_shown;
  force_premium = force_premium ?? post.force_premium;
  enforce_paywall_breaks = enforce_paywall_breaks ?? post.enforce_paywall_breaks;
  contains_subscriber_break = contains_subscriber_break ?? post.contains_subscriber_break;
  const currentSubscription = subscription || previewSubscription;

  const publicationHostname = useMemo(() => {
    try {
      return new URL(site.publication.url).hostname;
    } catch (_error: unknown) {
      // do nothing
    }
  }, [site.publication]);

  const { cookies, isLoggedIn: isLoggedInOutletValue, admin } = useOutletContext<OutletContext>();

  const isLoggedIn = isLoggedInOutletValue || (!!currentSubscription && isPreviewModal);
  const isPopupDisabled = Boolean(cookies.popup_closed) || isLoggedIn;
  const isGatedContentDisabled = Boolean(cookies.email_capture_disabled) || isLoggedIn;
  const isPostPremium = post.audience === 'premium';
  const isAdmin = Boolean(admin);
  const isSubscriptionPremium = currentSubscription?.tier === 'premium' && currentSubscription?.status !== 'paused';
  const hidePremiumComments = Boolean(isPostPremium && !isSubscriptionPremium && !isAdmin && !isDraft);
  const canShowCommentsNode = commentsEnabled && !hidePremiumComments;
  const showTruncated = contains_subscriber_break && !isGatedContentDisabled;

  const showSubscriberBreak = contains_subscriber_break && !isGatedContentDisabled;
  const showPaywallBreak = !!paywall_shown;
  const showPaywallAndHidePostContent = hide_premium_post;

  // Advanced Email Capture
  const isSubscriberGateEnabled =
    post.enforce_gated_content === true && !showSubscriberBreak && !isGatedContentDisabled;
  const isEmailCapturePopupEnabled =
    post.enable_popup_on_scroll === true && !showSubscriberBreak && !isGatedContentDisabled;

  // Recommended Reading State
  const showRecommendedPostsNode = !isDraft && !isSubscriberGateEnabled && !onlyRenderComments;

  // Audio block (text to speech)
  const showTextToSpeechBlock =
    !!post?.text_to_speech_config?.audio_url &&
    !isSubscriberGateEnabled &&
    !showSubscriberBreak &&
    !showPaywallBreak &&
    !showPaywallAndHidePostContent;

  const paywallUrl = useMemo(() => {
    const _searchParams = new URLSearchParams(location.search);

    if (paywall?.offer_id) {
      _searchParams.set('offer_id', paywall.offer_id);
    }
    const search = _searchParams.toString();
    return `/upgrade${search ? '?' : ''}${search}`;
  }, [paywall, location.search]);

  return (
    <PostPageContext.Provider
      value={{
        canShowCommentsNode,
        isGatedContentDisabled,
        onlyRenderComments,
        isPopupDisabled,
        hidePremiumComments,
        showRecommendedPostsNode,
        showTruncated,
        isSubscriberGateEnabled,
        isEmailCapturePopupEnabled,
        showSubscriberBreak,
        showPaywallBreak,
        showPaywallAndHidePostContent,
        paywallUrl,
        showTextToSpeechBlock,
        utmParams: {
          utmParamsEnabled: site.publication.utm_params_enabled,
          utmCampaign: site.publication.utm_campaign || post.parameterized_web_title,
          utmMedium: site.publication.utm_medium || 'referral',
          utmSource: site.publication.utm_source || publicationHostname,
        },
        forcePremium: force_premium,
        enforcePaywallBreaks: enforce_paywall_breaks,
      }}
    >
      {children}
    </PostPageContext.Provider>
  );
};
